import { useMutation, useQuery } from "@apollo/client";
import { PhoneNumberInput } from "@jasper/forms";
import { useUser } from "../../hooks/useUser";
import {
  CREATE_USER,
  ItemType,
  PHONE_NUMBER_DEFAULT_VALUE,
  SelectInputRef,
  STYLE,
  TextInputRef,
  UserRole,
  UserType,
  YesNoSelectorRef,
} from "@jasper/shared";
import { Button, Grid, Title } from "@mantine/core";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { UserStatus } from "/@/generated/graphql";

import { GET_ALL_GROUPS } from "../../gql/groups";

const UserEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { updateUser } = useUser();

  const { data: allGroups } = useQuery(GET_ALL_GROUPS, {
    variables: {
      skip: 0,
      take: 100,
    },
  });

  const [createUser] = useMutation(CREATE_USER);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues: {
      firstname: location.state?.user?.firstName,
      lastname: location.state?.user?.lastName,
      email: location.state?.user?.email,
      role: location.state?.user?.role,
      type: location.state?.user?.type,
      status: location.state?.user?.status ?? UserStatus.Active.toUpperCase(),
      isManager: location.state?.user?.isManager,
      group: location.state?.user?.userGroup?.id,
      phoneNumber:
        location.state?.user?.phoneNumber &&
        location.state?.user?.phoneNumber !== "NOT_PROVIDED"
          ? location.state.user.phoneNumber
          : "",
      communicateAccount: location.state?.user?.communicateAccount,
      meditAccount: location.state?.user?.meditAccount,
      shining3dAccount: location.state?.user?.shining3dAccount,
      dsCoreAccount: location.state?.user?.dsCoreAccount,
    },
  });

  const onSubmit = async data => {
    try {
      if (location?.state?.user?.id) {
        await updateUserSubmit(data);
        navigate(`/users/${location.state?.user?.id}`);
      } else {
        const req = await createUserSubmit(data);
        navigate(`/users/${req?.data?.createUser?.id}`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const updateUserSubmit = async data => {
    const res = await updateUser({
      variables: {
        where: {
          id: location.state?.user?.id,
        },
        data: {
          role: {
            set: data.role,
          },
          type: {
            set: data.type,
          },
          status: {
            set: data.status,
          },
          email: {
            set: data.email.trim(),
          },
          firstName: {
            set: data.firstname.trim(),
          },
          lastName: {
            set: data.lastname.trim(),
          },
          isManager: {
            set: data.isManager,
          },
          phoneNumber: {
            set: data.phoneNumber
              ? data.phoneNumber.trim()
              : PHONE_NUMBER_DEFAULT_VALUE,
          },
          communicateAccount: {
            set: data?.communicateAccount?.trim() || null,
          },
          meditAccount: {
            set: data?.meditAccount?.trim() || null,
          },
          shining3dAccount: {
            set: data?.shining3dAccount?.trim() || null,
          },
          dsCoreAccount: {
            set: data?.dsCoreAccount?.trim() || null,
          },
        },
      },
    });

    if (!res) throw new Error("Failed to update user");
  };

  const createUserSubmit = async data => {
    const res = await createUser({
      variables: {
        args: {
          firstName: data.firstname.trim(),
          email: data.email.trim(),
          lastName: data.lastname.trim(),
          type: data.type,
          phoneNumber: data.phoneNumber
            ? data.phoneNumber.trim()
            : PHONE_NUMBER_DEFAULT_VALUE,
          isManager: data.isManager,
          password: data.password.trim(),
          role: data.role,
          status: data.status,
          communicateAccount: data?.communicateAccount?.trim() || null,
          meditAccount: data?.meditAccount?.trim() || null,
          shining3dAccount: data?.shining3dAccount?.trim() || null,
          dsCoreAccount: data?.dsCoreAccount?.trim() || null,
          userGroup: location.state?.user?.userGroup?.id
            ? {
                connect: {
                  id: location.state?.user?.userGroup?.id,
                },
              }
            : undefined,
          productPreferences: {
            create: [
              {
                fixedProsthesisPreferences: {
                  createMany: {
                    data: [
                      {
                        itemType: ItemType.CROWN,
                      },
                      {
                        itemType: ItemType.LAY,
                      },
                      {
                        itemType: ItemType.BRIDGE,
                      },
                      {
                        itemType: ItemType.INLAY_CORE,
                      },
                      {
                        itemType: ItemType.IMPLANT,
                      },
                      {
                        itemType: ItemType.VENEER,
                      },
                      {
                        itemType: ItemType.BRIDGE_IMPLANT,
                      },
                    ],
                  },
                },
                removableProsthesisPreferences: {
                  create: [
                    {
                      itemType: ItemType.SPLINT,
                    },
                    {
                      itemType: ItemType.FULL_DENTURE,
                    },
                  ],
                },
              },
            ],
          },
        },
      },
    });
    if (!res) throw new Error("Failed to create user");
    return res;
  };

  return (
    <>
      <Title fw={200} style={{ paddingBottom: "1rem" }}>
        General
      </Title>
      <Grid>
        <Grid.Col span={6}>
          <TextInputRef
            name="firstname"
            label="Firstname"
            control={control}
            errors={errors}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInputRef
            name="lastname"
            label="Lastname"
            control={control}
            errors={errors}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        {!location.state?.user?.id && (
          <Grid.Col span={6}>
            <TextInputRef
              name="password"
              label="Password"
              control={control}
              errors={errors}
              secure={true}
            />
          </Grid.Col>
        )}
        <Grid.Col span={location.state?.user?.id ? 12 : 6}>
          <TextInputRef
            name="email"
            label="Email"
            control={control}
            errors={errors}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={6}>
          <SelectInputRef
            name="role"
            label="User role"
            data={Object.keys(UserRole)}
            control={control}
            errors={errors}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <SelectInputRef
            name="type"
            label="User type"
            data={Object.keys(UserType)}
            control={control}
            errors={errors}
          />
        </Grid.Col>
      </Grid>
      <Grid>
        <Grid.Col span={6}>
          <Controller
            control={control}
            name="phoneNumber"
            rules={{
              validate: value =>
                value
                  ? isValidPhoneNumber(value) ||
                    "This field must be a valid phone number"
                  : undefined,
            }}
            render={({ field, fieldState }) => {
              return (
                <PhoneNumberInput
                  value={field.value}
                  onChange={value => field.onChange(value || null)}
                  label="Phone Number"
                  error={fieldState.error?.message}
                />
              );
            }}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <SelectInputRef
            name="status"
            label="User status"
            data={Object.keys(UserStatus).map(status => status.toUpperCase())}
            control={control}
            errors={errors}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInputRef
            name="communicateAccount"
            label="Communicate Account"
            control={control}
            errors={errors}
            required={false}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInputRef
            name="meditAccount"
            label="MeditLink Account"
            control={control}
            errors={errors}
            required={false}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInputRef
            name="shining3dAccount"
            label="Shining3D Account"
            control={control}
            errors={errors}
            required={false}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInputRef
            name="dsCoreAccount"
            label="DS Core Account"
            control={control}
            errors={errors}
            required={false}
          />
        </Grid.Col>
      </Grid>
      <Title fw={200} style={{ paddingBottom: "1rem" }}>
        Clinic
      </Title>
      <Grid>
        <Grid.Col span={7}>
          <SelectInputRef
            name="group"
            label="User group (must be updated thought group settings)"
            data={(allGroups?.getAllGroups ?? []).map(group => ({
              label: group.name,
              value: group.id,
            }))}
            control={control}
            errors={errors}
            disabled
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <YesNoSelectorRef
            name="isManager"
            label="Is it a master account ?"
            value={location.state?.user?.isManager ?? false}
            setValue={value => setValue("isManager", value)}
          />
        </Grid.Col>
      </Grid>
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <Button
          style={{ backgroundColor: STYLE.primary }}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default UserEdit;
